/**
 * Builds a compact statistical summary of a client's training history
 * for injection into the AI prompt. Replaces dumping raw session data.
 */

export function buildClientHistorySummary(client) {
  const sessions = (client.sessions || []).filter(
    (s) => s.status === "done" || (!s.status && s.date),
  );

  if (sessions.length === 0) return "Aucun historique de séance.";

  const totalSessions = sessions.length;

  const exerciseStats = {};
  for (const s of sessions) {
    for (const ex of s.exercises || []) {
      if (!exerciseStats[ex.name]) {
        exerciseStats[ex.name] = { count: 0, weights: [], reps: [], volumes: [] };
      }
      const stat = exerciseStats[ex.name];
      stat.count++;
      if (ex.weight > 0) stat.weights.push(ex.weight);
      if (ex.reps > 0) stat.reps.push(ex.reps);
      const vol = (ex.sets || 1) * (ex.reps || 1) * (ex.weight || 1);
      stat.volumes.push(vol);
    }
  }

  const topExercises = Object.entries(exerciseStats)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, 6);

  const lines = [`Historique consolidé (${totalSessions} séances):`];

  const progressions = [];
  for (const [name, stat] of topExercises) {
    let detail = `${name} (×${stat.count})`;
    if (stat.weights.length >= 2) {
      const first = stat.weights[0];
      const last = stat.weights[stat.weights.length - 1];
      const max = Math.max(...stat.weights);
      const diff = last - first;
      const arrow = diff > 0 ? "↑" : diff < 0 ? "↓" : "→";
      detail += `: ${first}→${last}kg ${arrow}, PR ${max}kg`;
      if (diff !== 0) progressions.push({ name, diff, arrow });
    } else if (stat.reps.length >= 2) {
      const first = stat.reps[0];
      const last = stat.reps[stat.reps.length - 1];
      detail += `: ${first}→${last} reps`;
    }
    lines.push(`- ${detail}`);
  }

  if (progressions.length > 0) {
    const improving = progressions.filter((p) => p.diff > 0);
    const declining = progressions.filter((p) => p.diff < 0);
    if (improving.length)
      lines.push(
        `Tendance positive: ${improving.map((p) => `${p.name} (+${p.diff}kg)`).join(", ")}`,
      );
    if (declining.length)
      lines.push(
        `Attention baisse: ${declining.map((p) => `${p.name} (${p.diff}kg)`).join(", ")}`,
      );
  }

  const allVolumes = sessions.map((s) =>
    (s.exercises || []).reduce(
      (sum, ex) => sum + (ex.sets || 1) * (ex.reps || 1) * (ex.weight || 1),
      0,
    ),
  );
  if (allVolumes.length >= 3) {
    const recent3 = allVolumes.slice(-3);
    const older = allVolumes.slice(0, -3);
    if (older.length > 0) {
      const avgRecent = recent3.reduce((a, b) => a + b, 0) / recent3.length;
      const avgOlder = older.reduce((a, b) => a + b, 0) / older.length;
      const pct = Math.round(((avgRecent - avgOlder) / avgOlder) * 100);
      if (Math.abs(pct) > 5) {
        lines.push(
          `Volume global: ${pct > 0 ? "+" : ""}${pct}% vs séances précédentes`,
        );
      }
    }
  }

  return lines.join("\n");
}

/**
 * Builds a cross-client anonymous summary grouped by objective.
 * Helps the AI leverage patterns from other clients with similar goals.
 */
export function buildCrossClientSummary(clients, currentClient) {
  const similar = (clients || []).filter(
    (c) =>
      c.id !== currentClient.id &&
      c.status !== "archived" &&
      c.objective &&
      currentClient.objective &&
      hasSimilarObjective(c.objective, currentClient.objective),
  );

  if (similar.length < 2) return "";

  const allExercises = {};
  let totalSessions = 0;

  for (const c of similar) {
    const done = (c.sessions || []).filter(
      (s) => s.status === "done" || (!s.status && s.date),
    );
    totalSessions += done.length;
    for (const s of done) {
      for (const ex of s.exercises || []) {
        allExercises[ex.name] = (allExercises[ex.name] || 0) + 1;
      }
    }
  }

  if (totalSessions < 3) return "";

  const topEx = Object.entries(allExercises)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name]) => name);

  return `Patterns observés sur ${similar.length} clients avec objectif similaire (${totalSessions} séances) :\n- Exercices récurrents : ${topEx.join(", ")}`;
}

function hasSimilarObjective(a, b) {
  const normalize = (s) =>
    s
      .toLowerCase()
      .replace(/[^a-zàâéèêëïôùûüç\s]/g, "")
      .split(/\s+/);
  const wordsA = new Set(normalize(a));
  const wordsB = normalize(b);
  const keywords = new Set([
    "perte", "poids", "masse", "musculaire", "tonification", "mobilité",
    "souplesse", "endurance", "force", "remise", "forme", "cardio",
    "prévention", "rééducation", "performance",
  ]);
  const matchA = [...wordsA].filter((w) => keywords.has(w));
  const matchB = wordsB.filter((w) => keywords.has(w));
  return matchA.some((w) => matchB.includes(w));
}
